import { useMemo } from 'react'
import {
  useLocation,
  useNavigate,
  useSearchParams as useRouterSearchParams,
} from 'react-router-dom'

type AppHref =
  | string
  | URL
  | { pathname?: string; query?: Record<string, unknown>; hash?: string; search?: string }
type RouterNavigateState = {
  scroll?: boolean
}

type RouterOptions = {
  scroll?: boolean
}

function normalizeHref(href: AppHref): string {
  if (typeof href === 'string') {
    return href
  }

  if (href instanceof URL) {
    return `${href.pathname}${href.search}${href.hash}`
  }

  const basePath = href.pathname || '/'
  const searchParams = new URLSearchParams()

  if (href.search) {
    const query = href.search.startsWith('?') ? href.search.slice(1) : href.search
    query
      .split('&')
      .filter(Boolean)
      .forEach((entry) => {
        const [key, value = ''] = entry.split('=')
        if (key) {
          searchParams.append(decodeURIComponent(key), decodeURIComponent(value))
        }
      })
  }

  if (href.query && typeof href.query === 'object') {
    Object.entries(href.query).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((item) => {
          searchParams.append(key, String(item))
        })
      } else {
        searchParams.set(key, String(value))
      }
    })
  }

  const query = searchParams.toString()
  return `${basePath}${query ? `?${query}` : ''}${href.hash || ''}`
}

export function usePathname() {
  const location = useLocation()
  return location.pathname
}

export function useSearchParams() {
  const [searchParams] = useRouterSearchParams()
  return useMemo(() => {
    const copy = new URLSearchParams(searchParams)
    return copy
  }, [searchParams])
}

export function useRouter() {
  const navigate = useNavigate()
  const location = useLocation()

  return useMemo(
    () => ({
      push: (href: AppHref, _options?: RouterNavigateState) => {
        navigate(normalizeHref(href))
      },
      replace: (href: AppHref, _options?: RouterOptions) => {
        navigate(normalizeHref(href), { replace: true })
      },
      back: () => {
        navigate(-1)
      },
      forward: () => {
        navigate(1)
      },
      refresh: () => {
        if (typeof window !== 'undefined') {
          window.location.reload()
        }
      },
      prefetch: (_href: AppHref) => {
        return Promise.resolve()
      },
      pathname: location.pathname,
    }),
    [navigate, location.pathname]
  )
}
